import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import {
  fetchSensorFloorplanPlacements,
  fetchImageFloorplanPlacements,
  createFloorplanPlacement,
  updateFloorplanPlacement,
  deleteFloorplanPlacement,
} from '../floorplanPlacements/thunks/floorplanPlacements.thunks';
import { enqueueSnackbar } from 'notistack';
import i18n from 'i18next';

const placementsAdapter = createEntityAdapter({
  selectId: (placement) => placement.id,
});

const initialState = placementsAdapter.getInitialState({
  loading: false,
  error: null,
});

const floorplanPlacementsSlice = createSlice({
  name: 'floorplanPlacements',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch sensor placements
    builder
      .addCase(fetchSensorFloorplanPlacements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSensorFloorplanPlacements.fulfilled, (state, action) => {
        state.loading = false;
        placementsAdapter.setAll(state, action.payload);
      })
      .addCase(fetchSensorFloorplanPlacements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch image placements
    builder
      .addCase(fetchImageFloorplanPlacements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchImageFloorplanPlacements.fulfilled, (state, action) => {
        state.loading = false;
        // Add image placements (don't replace sensor placements)
        placementsAdapter.addMany(state, action.payload);
      })
      .addCase(fetchImageFloorplanPlacements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create placement
    builder
      .addCase(createFloorplanPlacement.pending, (state) => {
        state.error = null;
      })
      .addCase(createFloorplanPlacement.fulfilled, (state, action) => {
        const placements = Array.isArray(action.payload)
          ? action.payload
          : [action.payload];
        placementsAdapter.addMany(state, placements);
        enqueueSnackbar(i18n.t('gallery.placement_saved', 'Placement saved'), {
          variant: 'success',
        });
      })
      .addCase(createFloorplanPlacement.rejected, (state, action) => {
        state.error = action.payload;
        enqueueSnackbar(
          i18n.t('gallery.placement_save_error', 'Error saving placement'),
          {
            variant: 'error',
          }
        );
      });

    // Update placement
    builder
      .addCase(updateFloorplanPlacement.pending, (state) => {
        state.error = null;
      })
      .addCase(updateFloorplanPlacement.fulfilled, (state, action) => {
        const { id, ...changes } = action.payload;
        placementsAdapter.updateOne(state, {
          id,
          changes,
        });
      })
      .addCase(updateFloorplanPlacement.rejected, (state, action) => {
        state.error = action.payload;
        enqueueSnackbar(
          i18n.t('gallery.placement_update_error', 'Error updating placement'),
          {
            variant: 'error',
          }
        );
      });

    // Delete placement
    builder
      .addCase(deleteFloorplanPlacement.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteFloorplanPlacement.fulfilled, (state, action) => {
        placementsAdapter.removeOne(state, action.payload);
        enqueueSnackbar(
          i18n.t('gallery.placement_deleted', 'Placement deleted'),
          {
            variant: 'success',
          }
        );
      })
      .addCase(deleteFloorplanPlacement.rejected, (state, action) => {
        state.error = action.payload;
        enqueueSnackbar(
          i18n.t('gallery.placement_delete_error', 'Error deleting placement'),
          {
            variant: 'error',
          }
        );
      });
  },
});

export const placementSelectors = placementsAdapter.getSelectors(
  (state) => state.floorplanPlacements
);

export default floorplanPlacementsSlice.reducer;
